import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminIcon,
  FitnessCenter as CoachIcon,
  Person as ClientIcon,
  Storage as ArchIcon,
  Api as ApiIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { grey, primary } from '../../styles/tokens';

const sectionStyles = {
  card: {
    mb: 2,
    borderRadius: 2,
    border: `1px solid ${grey[200]}`,
    boxShadow: 'none',
  },
  sectionTitle: {
    fontWeight: 700,
    color: grey[900],
    mb: 1,
  },
  subtitle: {
    fontWeight: 600,
    color: grey[800],
    mb: 0.5,
  },
  body: {
    color: grey[600],
    fontSize: '0.875rem',
    lineHeight: 1.7,
  },
  chip: {
    fontSize: '0.75rem',
    height: 24,
  },
};

// ============================================
// TAB PANELS
// ============================================

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

// ============================================
// SECTION: ADMINISTRATEURS
// ============================================
const AdminSection = () => (
  <Box>
    <Card sx={sectionStyles.card}>
      <CardContent>
        <Typography variant="h6" sx={sectionStyles.sectionTitle}>
          Role et responsabilites
        </Typography>
        <Typography sx={sectionStyles.body}>
          L'administrateur (super_admin) gere l'ensemble de la plateforme YOCO. Il a acces a tous les outils de gestion, de moderation et de configuration. C'est le seul role capable de creer d'autres comptes admin, de gerer les coachs et de publier des recettes globales.
        </Typography>
      </CardContent>
    </Card>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Permissions disponibles</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Permission</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { perm: 'manage_admins', desc: 'Creer, lister et desactiver les comptes admin', status: 'active' },
                { perm: 'manage_coaches', desc: 'CRUD complet des coachs, suspension, reactivation, changement de plan', status: 'active' },
                { perm: 'manage_tenants', desc: 'Gestion des bases de donnees tenant (visualisation)', status: 'active' },
                { perm: 'manage_recipes', desc: 'CRUD des recettes globales, publication, import en masse', status: 'active' },
                { perm: 'view_analytics', desc: 'Consultation des statistiques de la plateforme', status: 'prevu' },
                { perm: 'manage_billing', desc: 'Gestion de la facturation et des abonnements', status: 'prevu' },
                { perm: 'manage_settings', desc: 'Configuration globale de la plateforme', status: 'prevu' },
              ].map((row) => (
                <TableRow key={row.perm}>
                  <TableCell><code style={{ fontSize: '0.8rem' }}>{row.perm}</code></TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status === 'active' ? 'Actif' : 'Prevu'}
                      size="small"
                      color={row.status === 'active' ? 'success' : 'default'}
                      sx={sectionStyles.chip}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Actions possibles</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Gestion des coachs</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['Lister les coachs', 'Creer un coach', 'Modifier un coach', 'Changer le plan', 'Suspendre', 'Reactiver', 'Supprimer', 'Voir les stats'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Gestion des recettes globales</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['Lister', 'Creer', 'Modifier', 'Supprimer', 'Publier', 'Depublier', 'Import en masse', 'Statistiques'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Waiting List</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['Lister les inscrits', 'Modifier statut', 'Supprimer', 'Exporter CSV', 'Statistiques'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Gestion des admins</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['Creer un admin', 'Lister les admins', 'Desactiver un admin'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Pages admin</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { page: 'Dashboard', route: '/admin', desc: 'Vue d\'ensemble avec stats et coachs recents' },
                { page: 'Liste des coachs', route: '/admin/coaches', desc: 'Tableau avec filtres (statut, plan, recherche)' },
                { page: 'Detail coach', route: '/admin/coaches/:id', desc: 'Profil complet, stats, actions' },
                { page: 'Formulaire coach', route: '/admin/coaches/new', desc: 'Creation / edition d\'un coach' },
                { page: 'Tenants', route: '/admin/tenants', desc: 'Liste des bases de donnees tenant' },
                { page: 'Waiting List', route: '/admin/waiting-list', desc: 'Gestion des inscrits a la waiting list' },
                { page: 'Recettes', route: '/admin/recipes', desc: 'Bibliotheque de recettes globales' },
                { page: 'Profil', route: '/admin/profile', desc: 'Modification du profil admin' },
                { page: 'Documentation', route: '/admin/documentation', desc: 'Cette page' },
              ].map((row) => (
                <TableRow key={row.route}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.page}</TableCell>
                  <TableCell><code style={{ fontSize: '0.8rem' }}>{row.route}</code></TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  </Box>
);

// ============================================
// SECTION: COACHS
// ============================================
const CoachSection = () => (
  <Box>
    <Card sx={sectionStyles.card}>
      <CardContent>
        <Typography variant="h6" sx={sectionStyles.sectionTitle}>
          Role et responsabilites
        </Typography>
        <Typography sx={sectionStyles.body}>
          Le coach est l'utilisateur B2B principal de YOCO. Chaque coach dispose de sa propre base de donnees isolee (multi-tenant). Il gere ses clients, cree des recettes et des plans alimentaires hebdomadaires. Le coach peut s'abonner via Stripe pour debloquer des fonctionnalites avancees.
        </Typography>
      </CardContent>
    </Card>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Plans d'abonnement</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Max clients</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Prix</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Fonctionnalites</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="Free" size="small" sx={sectionStyles.chip} /></TableCell>
                <TableCell>5</TableCell>
                <TableCell>Gratuit</TableCell>
                <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>Clients, recettes, plans de base</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Starter" size="small" color="primary" sx={sectionStyles.chip} /></TableCell>
                <TableCell>10+</TableCell>
                <TableCell>19EUR/mois</TableCell>
                <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>Toutes fonctionnalites, support prioritaire</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Pro" size="small" color="secondary" sx={sectionStyles.chip} /></TableCell>
                <TableCell>Illimite</TableCell>
                <TableCell>Sur devis</TableCell>
                <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>White-label, analytics, API</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="Enterprise" size="small" color="warning" sx={sectionStyles.chip} /></TableCell>
                <TableCell>Illimite</TableCell>
                <TableCell>Sur devis</TableCell>
                <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>Tout inclus, support dedie</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Actions possibles</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Gestion des clients</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['Lister', 'Creer', 'Modifier', 'Supprimer', 'Import en masse', 'Suivi de poids', 'Recalculer macros', 'Activer portail'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Recettes</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['Creer', 'Modifier', 'Supprimer', 'Favori', 'Copier recette globale', 'Rechercher CIQUAL'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Plans alimentaires</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['Creer', 'Modifier', 'Supprimer', 'Publier', 'Dupliquer', 'Commenter', 'Liste de courses'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Nutrition (CIQUAL)</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['Calculer macros', 'Calculer BMR/DEJ', 'Rechercher aliments', 'Ajuster recette', 'Ajuster journee'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Abonnement</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['S\'abonner (Stripe)', 'Gerer abonnement', 'Portail facturation', 'Annuler'].map(a => (
              <Chip key={a} label={a} size="small" sx={sectionStyles.chip} />
            ))}
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Pages coach</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { page: 'Dashboard', route: '/coach', desc: 'Stats, clients actifs, actions rapides' },
                { page: 'Liste clients', route: '/coach/clients', desc: 'Tous les clients avec filtres' },
                { page: 'Creer client', route: '/coach/clients/create', desc: 'Formulaire de creation' },
                { page: 'Voir client', route: '/coach/clients/view/:id', desc: 'Profil complet du client' },
                { page: 'Modifier client', route: '/coach/clients/edit/:id', desc: 'Modification du profil' },
                { page: 'Liste programmes', route: '/coach/plans', desc: 'Tous les plans hebdomadaires' },
                { page: 'Creer programme', route: '/coach/plans/create', desc: 'Nouveau plan alimentaire' },
                { page: 'Voir programme', route: '/coach/plans/view/:clientId/:weekPlanId', desc: 'Detail du plan' },
                { page: 'Modifier programme', route: '/coach/plans/edit/:clientId/:weekPlanId', desc: 'Edition du plan' },
                { page: 'Recettes', route: '/coach/recipes', desc: 'Bibliotheque de recettes coach' },
                { page: 'Creer recette', route: '/coach/recipes/create', desc: 'Nouvelle recette' },
                { page: 'Profil', route: '/coach/profile', desc: 'Profil et abonnement' },
              ].map((row) => (
                <TableRow key={row.route}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.page}</TableCell>
                  <TableCell><code style={{ fontSize: '0.8rem' }}>{row.route}</code></TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Donnees du coach (modele)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Champ</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { field: 'email', type: 'String', desc: 'Email unique du coach' },
                { field: 'firstName / lastName', type: 'String', desc: 'Nom et prenom' },
                { field: 'phone', type: 'String', desc: 'Telephone' },
                { field: 'tenantId', type: 'String', desc: 'Identifiant de la base de donnees isolee' },
                { field: 'tenantStatus', type: 'Enum', desc: 'pending, provisioning, active, suspended, deleted' },
                { field: 'subscriptionPlan', type: 'Enum', desc: 'free, starter, pro, enterprise' },
                { field: 'maxClients', type: 'Number', desc: 'Limite de clients selon le plan (defaut: 5)' },
                { field: 'isSubscribed', type: 'Boolean', desc: 'Statut de l\'abonnement Stripe' },
                { field: 'stripeCustomerId', type: 'String', desc: 'ID client Stripe' },
                { field: 'isActive', type: 'Boolean', desc: 'Compte actif ou non' },
                { field: 'settings', type: 'Object', desc: 'brandName, logo, primaryColor, timezone, language' },
              ].map((row) => (
                <TableRow key={row.field}>
                  <TableCell><code style={{ fontSize: '0.8rem' }}>{row.field}</code></TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{row.type}</TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  </Box>
);

// ============================================
// SECTION: CLIENTS
// ============================================
const ClientSection = () => (
  <Box>
    <Card sx={sectionStyles.card}>
      <CardContent>
        <Typography variant="h6" sx={sectionStyles.sectionTitle}>
          Role et responsabilites
        </Typography>
        <Typography sx={sectionStyles.body}>
          Le client est l'utilisateur final de YOCO. Il est cree et gere par son coach dans la base de donnees tenant du coach. Le client a un acces en lecture seule a son portail mobile-first : il peut consulter ses plans alimentaires, voir les recettes et generer sa liste de courses. Le coach doit activer l'acces portail (hasPortalAccess) pour que le client puisse se connecter.
        </Typography>
      </CardContent>
    </Card>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Acces au portail</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={sectionStyles.body} paragraph>
          Pour qu'un client puisse se connecter au portail :
        </Typography>
        <Box component="ol" sx={{ pl: 2, ...sectionStyles.body }}>
          <li>Le coach cree le client via son interface</li>
          <li>Le coach active <code>hasPortalAccess = true</code></li>
          <li>Le coach definit un mot de passe pour le client</li>
          <li>Le client se connecte sur la meme page de login (detection automatique coach/client)</li>
          <li>Le client est redirige vers <code>/client</code></li>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Conditions requises</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="hasPortalAccess = true" size="small" color="success" sx={sectionStyles.chip} />
            <Chip label="Mot de passe defini" size="small" color="success" sx={sectionStyles.chip} />
            <Chip label="Statut actif" size="small" color="success" sx={sectionStyles.chip} />
            <Chip label="Coach actif" size="small" color="success" sx={sectionStyles.chip} />
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Fonctionnalites du portail client</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Page</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { page: 'Accueil', route: '/client', desc: 'Resume du plan de la semaine, objectifs caloriques, recommandations du coach' },
                { page: 'Repas', route: '/client/meals', desc: 'Navigation par jour (carousel), detail des repas et recettes, macros par repas' },
                { page: 'Courses', route: '/client/shopping', desc: 'Liste de courses generee automatiquement, categorisee, avec checklist persistante' },
                { page: 'Profil', route: '/client/profile', desc: 'Informations personnelles (lecture seule), donnees physiques, macros cibles, coach assigne' },
              ].map((row) => (
                <TableRow key={row.route}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.page}</TableCell>
                  <TableCell><code style={{ fontSize: '0.8rem' }}>{row.route}</code></TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Restrictions</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="Lecture seule" size="small" color="warning" sx={sectionStyles.chip} />
            <Chip label="Pas de modification des plans" size="small" color="warning" sx={sectionStyles.chip} />
            <Chip label="Pas d'ajout de recettes" size="small" color="warning" sx={sectionStyles.chip} />
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Donnees du client (modele)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Champ</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { field: 'email', type: 'String', desc: 'Email unique (dans le tenant)' },
                { field: 'firstName / lastName', type: 'String', desc: 'Nom et prenom' },
                { field: 'weight / height / age', type: 'Number', desc: 'Donnees physiques' },
                { field: 'gender', type: 'Enum', desc: 'male, female, other' },
                { field: 'activityLevel', type: 'Enum', desc: 'sedentary, lightly_active, moderate, active, very_active' },
                { field: 'goalType', type: 'Enum', desc: 'weight_loss, weight_gain, maintenance, muscle_gain, health, performance' },
                { field: 'dietaryPreferences', type: 'Array', desc: 'vegetarian, vegan, gluten-free, dairy-free, paleo, keto...' },
                { field: 'intolerances', type: 'Array', desc: '18 types : gluten, lactose, nuts, shellfish...' },
                { field: 'targetMacros', type: 'Object', desc: 'calories, carbohydrates, proteins, fats' },
                { field: 'sportProfile', type: 'Object', desc: 'nap (1.0-2.5), period (base, intensification, competition)' },
                { field: 'weightHistory', type: 'Array', desc: 'Historique de poids (weight, date, note)' },
                { field: 'status', type: 'Enum', desc: 'active, inactive, paused, archived' },
                { field: 'hasPortalAccess', type: 'Boolean', desc: 'Active/desactive l\'acces portail client' },
              ].map((row) => (
                <TableRow key={row.field}>
                  <TableCell><code style={{ fontSize: '0.8rem' }}>{row.field}</code></TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{row.type}</TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  </Box>
);

// ============================================
// SECTION: ARCHITECTURE
// ============================================
const ArchitectureSection = () => (
  <Box>
    <Card sx={sectionStyles.card}>
      <CardContent>
        <Typography variant="h6" sx={sectionStyles.sectionTitle}>
          Architecture multi-tenant
        </Typography>
        <Typography sx={sectionStyles.body}>
          YOCO utilise une architecture multi-tenant avec isolation par base de donnees. Chaque coach dispose de sa propre base MongoDB qui contient ses clients, recettes et plans alimentaires. Les donnees globales (admins, coachs, recettes globales, tenants) sont stockees dans la base master.
        </Typography>
      </CardContent>
    </Card>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Base de donnees Master</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={sectionStyles.body} paragraph>
          La base master contient les collections globales de la plateforme :
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Modele</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { model: 'Admin', desc: 'Administrateurs de la plateforme' },
                { model: 'Coach', desc: 'Utilisateurs B2B (coachs nutrition)' },
                { model: 'Tenant', desc: 'Metadata des bases tenant (limites, features, stats)' },
                { model: 'GlobalRecipe', desc: 'Recettes partagees entre tous les coachs' },
                { model: 'WaitingListEntry', desc: 'Inscriptions a la waiting list (landing page)' },
              ].map((row) => (
                <TableRow key={row.model}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.model}</TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Base de donnees Tenant (par coach)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={sectionStyles.body} paragraph>
          Chaque coach a une base de donnees isolee contenant :
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Modele</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { model: 'Client', desc: 'Clients geres par le coach' },
                { model: 'CoachRecipe', desc: 'Recettes personnelles du coach (+ copies de recettes globales)' },
                { model: 'WeekPlan', desc: 'Plans alimentaires hebdomadaires pour les clients' },
              ].map((row) => (
                <TableRow key={row.model}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.model}</TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.desc}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Authentification (JWT)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={sectionStyles.body} paragraph>
          L'authentification est basee sur des JWT (JSON Web Tokens) avec un champ <code>type</code> qui determine le role de l'utilisateur :
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Base de donnees</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Middleware</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contenu du token</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><Chip label="admin" size="small" color="error" sx={sectionStyles.chip} /></TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>Master</TableCell>
                <TableCell><code style={{ fontSize: '0.8rem' }}>protect + admin</code></TableCell>
                <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>id, role, permissions</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="coach" size="small" color="primary" sx={sectionStyles.chip} /></TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>Master</TableCell>
                <TableCell><code style={{ fontSize: '0.8rem' }}>protect + coach</code></TableCell>
                <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>id, tenantId, tenantDbName, subscriptionPlan, maxClients</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><Chip label="client" size="small" color="success" sx={sectionStyles.chip} /></TableCell>
                <TableCell sx={{ fontSize: '0.85rem' }}>Tenant</TableCell>
                <TableCell><code style={{ fontSize: '0.8rem' }}>protect + client</code></TableCell>
                <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>id, tenantId, tenantDbName</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Flux de login unifie</Typography>
          <Typography sx={sectionStyles.body}>
            La page de login est partagee entre coachs et clients. Le backend detecte automatiquement le type d'utilisateur en cherchant d'abord dans la table Coach (master DB), puis dans les tables Client de chaque tenant actif.
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Stack technique</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Composant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Technologie</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { comp: 'Frontend', tech: 'React 18 + Material-UI (MUI)' },
                { comp: 'Backend', tech: 'Node.js + Express.js' },
                { comp: 'Base de donnees', tech: 'MongoDB (Mongoose ODM)' },
                { comp: 'Auth', tech: 'JWT + bcrypt' },
                { comp: 'Paiement', tech: 'Stripe (Checkout, Subscriptions, Webhooks)' },
                { comp: 'Email', tech: 'Resend API' },
                { comp: 'Nutrition', tech: 'CIQUAL (3184 aliments, base francaise)' },
                { comp: 'Hebergement', tech: 'Render (backend) + Vercel (frontend/landing)' },
                { comp: 'Landing page', tech: 'Vue 3 + Vite' },
              ].map((row) => (
                <TableRow key={row.comp}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.comp}</TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{row.tech}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>

    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>Securite</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {[
            'Mot de passe hashe avec bcrypt (10 salt rounds)',
            'Tokens JWT avec expiration (30 jours)',
            'Isolation multi-tenant par base de donnees',
            'RBAC (Role-Based Access Control) avec permissions granulaires',
            'Verification du statut du compte a chaque requete',
            'CORS configure par domaine autorise',
            'Verification de signature Stripe pour les webhooks',
            'Reset de mot de passe avec token SHA256 (expire en 30 min)',
          ].map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography sx={{ ...sectionStyles.body, mb: 0 }}>{item}</Typography>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  </Box>
);

// ============================================
// SECTION: API ENDPOINTS
// ============================================
const ApiSection = () => {
  const EndpointGroup = ({ title, endpoints }) => (
    <Accordion sx={sectionStyles.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={sectionStyles.subtitle}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>Methode</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Endpoint</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }}>Auth</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {endpoints.map((ep, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Chip
                      label={ep.method}
                      size="small"
                      sx={{
                        ...sectionStyles.chip,
                        fontWeight: 600,
                        bgcolor: ep.method === 'GET' ? '#E3F2FD' : ep.method === 'POST' ? '#E8F5E9' : ep.method === 'PUT' ? '#FFF3E0' : '#FFEBEE',
                        color: ep.method === 'GET' ? '#1565C0' : ep.method === 'POST' ? '#2E7D32' : ep.method === 'PUT' ? '#E65100' : '#C62828',
                      }}
                    />
                  </TableCell>
                  <TableCell><code style={{ fontSize: '0.75rem' }}>{ep.path}</code></TableCell>
                  <TableCell sx={{ color: grey[600], fontSize: '0.85rem' }}>{ep.desc}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{ep.auth}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box>
      <Card sx={sectionStyles.card}>
        <CardContent>
          <Typography variant="h6" sx={sectionStyles.sectionTitle}>
            API Reference
          </Typography>
          <Typography sx={sectionStyles.body}>
            Liste complete des endpoints de l'API YOCO. Tous les endpoints proteges necessitent un header <code>Authorization: Bearer {'<token>'}</code>.
          </Typography>
        </CardContent>
      </Card>

      <EndpointGroup title="Admin - Authentification" endpoints={[
        { method: 'POST', path: '/api/admin/auth/login', desc: 'Connexion admin', auth: 'Public' },
        { method: 'GET', path: '/api/admin/auth/profile', desc: 'Profil admin', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/auth/profile', desc: 'Modifier profil', auth: 'Admin' },
        { method: 'POST', path: '/api/admin/auth/create', desc: 'Creer un admin', auth: 'Admin (manage_admins)' },
        { method: 'GET', path: '/api/admin/auth/list', desc: 'Lister les admins', auth: 'Admin (manage_admins)' },
        { method: 'POST', path: '/api/admin/auth/refresh-token', desc: 'Rafraichir le token', auth: 'Admin' },
      ]} />

      <EndpointGroup title="Admin - Gestion des coachs" endpoints={[
        { method: 'GET', path: '/api/admin/coaches', desc: 'Lister les coachs', auth: 'Admin' },
        { method: 'POST', path: '/api/admin/coaches', desc: 'Creer un coach', auth: 'Admin' },
        { method: 'GET', path: '/api/admin/coaches/:id', desc: 'Detail du coach', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/coaches/:id', desc: 'Modifier un coach', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/coaches/:id/plan', desc: 'Changer le plan', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/coaches/:id/suspend', desc: 'Suspendre', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/coaches/:id/reactivate', desc: 'Reactiver', auth: 'Admin' },
        { method: 'DELETE', path: '/api/admin/coaches/:id', desc: 'Supprimer', auth: 'Admin' },
        { method: 'GET', path: '/api/admin/coaches/:id/stats', desc: 'Stats du coach', auth: 'Admin' },
      ]} />

      <EndpointGroup title="Admin - Recettes globales" endpoints={[
        { method: 'GET', path: '/api/admin/recipes', desc: 'Lister les recettes', auth: 'Admin' },
        { method: 'POST', path: '/api/admin/recipes', desc: 'Creer une recette', auth: 'Admin' },
        { method: 'GET', path: '/api/admin/recipes/:id', desc: 'Detail recette', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/recipes/:id', desc: 'Modifier', auth: 'Admin' },
        { method: 'DELETE', path: '/api/admin/recipes/:id', desc: 'Supprimer', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/recipes/:id/publish', desc: 'Publier', auth: 'Admin' },
        { method: 'POST', path: '/api/admin/recipes/bulk', desc: 'Import en masse', auth: 'Admin' },
        { method: 'GET', path: '/api/admin/recipes/stats', desc: 'Statistiques', auth: 'Admin' },
      ]} />

      <EndpointGroup title="Admin - Waiting List" endpoints={[
        { method: 'GET', path: '/api/admin/waiting-list', desc: 'Lister les inscrits', auth: 'Admin' },
        { method: 'GET', path: '/api/admin/waiting-list/stats', desc: 'Statistiques', auth: 'Admin' },
        { method: 'PUT', path: '/api/admin/waiting-list/:id', desc: 'Modifier une entree', auth: 'Admin' },
        { method: 'DELETE', path: '/api/admin/waiting-list/:id', desc: 'Supprimer', auth: 'Admin' },
        { method: 'GET', path: '/api/admin/waiting-list/export', desc: 'Exporter CSV', auth: 'Admin' },
      ]} />

      <EndpointGroup title="Coach - Authentification" endpoints={[
        { method: 'POST', path: '/api/coach/auth/register', desc: 'Inscription coach', auth: 'Public' },
        { method: 'POST', path: '/api/coach/auth/login', desc: 'Connexion coach', auth: 'Public' },
        { method: 'POST', path: '/api/coach/auth/unified-login', desc: 'Login unifie (coach/client)', auth: 'Public' },
        { method: 'GET', path: '/api/coach/auth/profile', desc: 'Profil coach', auth: 'Coach' },
        { method: 'PUT', path: '/api/coach/auth/profile', desc: 'Modifier profil', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/auth/checkout', desc: 'Checkout Stripe', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/auth/customer-portal', desc: 'Portail facturation', auth: 'Coach' },
      ]} />

      <EndpointGroup title="Coach - Clients" endpoints={[
        { method: 'GET', path: '/api/coach/clients', desc: 'Lister les clients', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/clients', desc: 'Creer un client', auth: 'Coach' },
        { method: 'GET', path: '/api/coach/clients/:id', desc: 'Detail client', auth: 'Coach' },
        { method: 'PUT', path: '/api/coach/clients/:id', desc: 'Modifier', auth: 'Coach' },
        { method: 'DELETE', path: '/api/coach/clients/:id', desc: 'Supprimer', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/clients/bulk', desc: 'Import en masse', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/clients/:id/weight', desc: 'Ajouter poids', auth: 'Coach' },
        { method: 'GET', path: '/api/coach/clients/:id/weight-history', desc: 'Historique poids', auth: 'Coach' },
      ]} />

      <EndpointGroup title="Coach - Recettes" endpoints={[
        { method: 'GET', path: '/api/coach/recipes', desc: 'Mes recettes', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/recipes', desc: 'Creer une recette', auth: 'Coach' },
        { method: 'PUT', path: '/api/coach/recipes/:id', desc: 'Modifier', auth: 'Coach' },
        { method: 'DELETE', path: '/api/coach/recipes/:id', desc: 'Supprimer', auth: 'Coach' },
        { method: 'GET', path: '/api/coach/recipes/global', desc: 'Recettes globales', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/recipes/global/:id/copy', desc: 'Copier recette globale', auth: 'Coach' },
        { method: 'PUT', path: '/api/coach/recipes/:id/favorite', desc: 'Favori', auth: 'Coach' },
      ]} />

      <EndpointGroup title="Coach - Plans alimentaires" endpoints={[
        { method: 'GET', path: '/api/coach/weekplans', desc: 'Tous les plans', auth: 'Coach' },
        { method: 'GET', path: '/api/coach/weekplans/:id', desc: 'Detail plan', auth: 'Coach' },
        { method: 'PUT', path: '/api/coach/weekplans/:id', desc: 'Modifier', auth: 'Coach' },
        { method: 'DELETE', path: '/api/coach/weekplans/:id', desc: 'Supprimer', auth: 'Coach' },
        { method: 'PUT', path: '/api/coach/weekplans/:id/publish', desc: 'Publier', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/weekplans/:id/duplicate', desc: 'Dupliquer', auth: 'Coach' },
        { method: 'GET', path: '/api/coach/clients/:clientId/weekplans', desc: 'Plans du client', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/clients/:clientId/weekplans', desc: 'Creer pour client', auth: 'Coach' },
      ]} />

      <EndpointGroup title="Coach - Nutrition (CIQUAL)" endpoints={[
        { method: 'POST', path: '/api/coach/nutrition/calculate-macros', desc: 'Calculer macros journalieres', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/nutrition/calculate-all-macros', desc: 'Macros tous types de jour', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/nutrition/bmr-dej', desc: 'Calculer BMR et DEJ', auth: 'Coach' },
        { method: 'GET', path: '/api/coach/nutrition/ciqual/search', desc: 'Rechercher aliment CIQUAL', auth: 'Coach' },
        { method: 'POST', path: '/api/coach/nutrition/adjust-recipe', desc: 'Ajuster portions recette', auth: 'Coach' },
        { method: 'GET', path: '/api/coach/nutrition/shopping-list/:weekPlanId', desc: 'Liste de courses', auth: 'Coach' },
      ]} />

      <EndpointGroup title="Client - Portail" endpoints={[
        { method: 'GET', path: '/api/client/profile', desc: 'Mon profil', auth: 'Client' },
        { method: 'GET', path: '/api/client/profile/coach', desc: 'Info coach', auth: 'Client' },
        { method: 'GET', path: '/api/client/weekplan/current', desc: 'Plan actif', auth: 'Client' },
        { method: 'GET', path: '/api/client/weekplan', desc: 'Mes plans', auth: 'Client' },
        { method: 'GET', path: '/api/client/weekplan/:id', desc: 'Detail plan', auth: 'Client' },
      ]} />

      <EndpointGroup title="Public" endpoints={[
        { method: 'POST', path: '/api/waiting-list', desc: 'Inscription waiting list', auth: 'Public' },
        { method: 'POST', path: '/api/webhook/stripe', desc: 'Webhook Stripe', auth: 'Signature Stripe' },
        { method: 'GET', path: '/health', desc: 'Health check', auth: 'Public' },
      ]} />
    </Box>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const Documentation = () => {
  const [tabValue, setTabValue] = useState(0);

  const tabs = [
    { label: 'Admins', icon: <AdminIcon />, component: <AdminSection /> },
    { label: 'Coachs', icon: <CoachIcon />, component: <CoachSection /> },
    { label: 'Clients', icon: <ClientIcon />, component: <ClientSection /> },
    { label: 'Architecture', icon: <ArchIcon />, component: <ArchitectureSection /> },
    { label: 'API', icon: <ApiIcon />, component: <ApiSection /> },
  ];

  return (
    <Box>
      <Helmet>
        <title>Documentation - YOCO Admin</title>
      </Helmet>

      <Typography variant="h5" sx={{ fontWeight: 700, color: grey[900], mb: 0.5 }}>
        Documentation
      </Typography>
      <Typography sx={{ color: grey[500], mb: 3, fontSize: '0.9rem' }}>
        Guide complet du fonctionnement de la plateforme YOCO 2.0
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 1,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 44,
          },
          '& .Mui-selected': {
            fontWeight: 600,
          },
        }}
      >
        {tabs.map((tab, i) => (
          <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
        ))}
      </Tabs>

      <Divider sx={{ mb: 0 }} />

      {tabs.map((tab, i) => (
        <TabPanel key={i} value={tabValue} index={i}>
          {tab.component}
        </TabPanel>
      ))}
    </Box>
  );
};

export default Documentation;
